import Button from '@components/_Revamp/Button';
import Icon from '@components/_Revamp/Icon';
import { InputGroup } from '@components/_Revamp/InputGroup';
import Typography from '@components/_Revamp/Typography';
import { styled } from '@linaria/react';
import { useAvivaSelector } from '@redux/hook';
import React, { useEffect, useRef, useState } from 'react';
import { ITheme, useTheme } from 'src/theme-revamp';

const BmiValue = styled.p<{ theme?: ITheme }>`
  color: ${(props) => props.theme?.colors.grey[1400]};
  font-weight: 400;
  font-size: 16px;
  line-height: 1.4;
  span {
    color: ${(props) => props.theme?.colors.primary!};
    font-weight: 600;
  }
`;

const BMIContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 12px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 16px;
  margin: 0 0 64px 0;
`;

const InputContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  margin-bottom: 80px;
`;

const HeightAndWeightContainer = styled.div`
  max-width: 588px;
  margin: 0 auto 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media screen and (max-width: 768px) {
    margin: 0 auto 0 auto;
  }
`;

export interface HeightAndWeightModel {
  height: number;
  weight: number;
}
export interface HeightAndWeightProps {
  values?: HeightAndWeightModel;
  onComplete: (heightAndWeight: HeightAndWeightModel) => void;
}

export const HeightAndWeight = ({
  values,
  onComplete,
}: HeightAndWeightProps) => {
  const theme = useTheme();
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [weight, setWeight] = useState<number | undefined>(undefined);
  const heightInputRef = useRef<HTMLInputElement>(null);
  const weightInputRef = useRef<HTMLInputElement>(null);

  const [BMI, setBMI] = useState<number | null>(null);
  const [isValidInput, setIsValidInput] = useState(false);
  const resetQRValues = useAvivaSelector((state) => state.mltcR3Step5.resetQRData);  

  const btnGotIt_Clicked = () => {
    if (height && weight) onComplete({ height, weight });
  };

  const HeightInput_Changed = (e: any) => {
    let value = e.target.value.replace(".", "");
    setHeight(value);
    if(e.target.value.length >= 3 && weightInputRef.current) {  
      weightInputRef.current?.focus();
    }
  };
  
  const WeightInput_Changed = (e: any) => {
    let value = e.target.value.replace(".", "");
    setWeight(value);
    if(e.target.value.length >= 3 && weightInputRef.current) {  
      weightInputRef.current?.blur();
    }
  };

  useEffect(() => {
    if (values && values.height && values.weight) {
      setHeight(values.height);
      setWeight(values.weight);
    }
  }, [values]);

  useEffect(() => {
    if (height && weight) {
      const bmiValue = Math.floor(weight / Math.pow(height / 100, 2));
      setBMI(bmiValue);
      setIsValidInput(true);
    }
  }, [height, weight]);

  useEffect(()=>{
    if(resetQRValues){
      setHeight(undefined);
      setWeight(undefined);
    }    
  },[resetQRValues])

  
  return (
    <HeightAndWeightContainer>
      <Header>
        <Typography variant="title" align="center" maxWidht={588}>
          Let's get to know you better, what is your height and weight?
        </Typography>
        {BMI ? (
          <BMIContainer>
            <Icon name="alert-circle-grey" />
            <BmiValue theme={theme}>
              Just a heads up, your <span>BMI is {BMI}</span>
            </BmiValue>
          </BMIContainer>
        ) : (
          <Typography variant="description" align="center" maxWidht={400}>
            We'll let you on a quick fact about your body that you don't need
            to see a doctor for!
          </Typography>
        )}
      </Header>

      <InputContainer>
        <InputGroup
          ref={heightInputRef}
          id="height"
          label="Height(cm)"
          placeholder="180"
          onChange={HeightInput_Changed}
          pattern="\d*"
          inputProps={{
            type: 'number',
            value: height,
            maxLength: 3,
          }} 
          inputType={'input'}
        />
        <InputGroup
          ref={weightInputRef}
          id="weight"
          label="Weight(kg)"
          placeholder="75"
          pattern="\d*"
          onChange={WeightInput_Changed}
          inputProps={{
            type: 'number',
            value: weight,
            maxLength: 3,
          }} 
          inputType={'input'} 
          />
      </InputContainer>
      <Button
        label="Got it"
        width="180px"
        onClick={btnGotIt_Clicked}
        disabled={!isValidInput}
      />
    </HeightAndWeightContainer>
  );
};
